import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, ShieldCheck, Warehouse, KeyRound, Plus, RefreshCw, AlertCircle, Lock, UserPlus, Search,
} from 'lucide-react';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  backoffice: 'bg-slate-100 text-slate-700 border-slate-200',
  procurement_manager: 'bg-blue-100 text-blue-700 border-blue-200',
  stock_manager: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  warehouse_operator: 'bg-amber-100 text-amber-700 border-amber-200',
  store_manager: 'bg-purple-100 text-purple-700 border-purple-200',
  store_operator: 'bg-violet-100 text-violet-700 border-violet-200',
  technician: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  store: 'bg-purple-100 text-purple-700 border-purple-200', // Legacy
};

export default function UserManagement() {
  const { user: me, hasPermission } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({ roles: [], permissions: [] });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState(null); // user being edited
  const [editTab, setEditTab] = useState('role');
  const [pendingRole, setPendingRole] = useState('');
  const [pendingScope, setPendingScope] = useState([]);
  const [pendingAllow, setPendingAllow] = useState([]);
  const [pendingDeny, setPendingDeny] = useState([]);
  const [pendingPassword, setPendingPassword] = useState('');
  const [pendingFullName, setPendingFullName] = useState('');
  const [pendingActive, setPendingActive] = useState(true);
  const [pendingDefaultWarehouse, setPendingDefaultWarehouse] = useState('');
  const [pendingReferenceWarehouse, setPendingReferenceWarehouse] = useState('');
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [createForm, setCreateForm] = useState({
    email: '', password: '', full_name: '', role: 'technician',
    allowed_warehouses: [],
  });

  const canManage = hasPermission('users.manage');

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [u, c, w] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/admin/permissions/catalog`),
        axios.get(`${API}/warehouses`),
      ]);
      setUsers(u.data.users || []);
      setCatalog(c.data || { roles: [], permissions: [] });
      setWarehouses(w.data.warehouses || w.data || []);
    } catch (e) {
      toast.error('Erro ao carregar dados de utilizadores');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rolesBySlug = useMemo(() => {
    const m = {};
    (catalog.roles || []).forEach((r) => { m[r.slug] = r; });
    return m;
  }, [catalog]);

  const permMap = useMemo(() => {
    const m = {};
    (catalog.permissions || []).forEach((p) => { m[p.slug] = p.label; });
    return m;
  }, [catalog]);

  const openEdit = (u) => {
    setEditing(u);
    setPendingRole(u.role);
    setPendingScope([...(u.allowed_warehouses || [])]);
    setPendingAllow([...((u.permission_overrides || {}).allow || [])]);
    setPendingDeny([...((u.permission_overrides || {}).deny || [])]);
    setPendingPassword('');
    setPendingFullName(u.full_name || '');
    setPendingActive(u.is_active !== false);
    setPendingDefaultWarehouse(u.default_warehouse || '');
    setPendingReferenceWarehouse(u.reference_warehouse || '');
    setEditTab('role');
  };

  const saveRole = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${editing.id}/role`, { role: pendingRole });
      toast.success(`Role atualizado para ${pendingRole}`);
      fetchAll();
      // Update local "editing" too
      setEditing((prev) => prev ? { ...prev, role: pendingRole } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar role');
    } finally { setSaving(false); }
  };

  const saveScope = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${editing.id}/warehouse-scope`, {
        allowed_warehouses: pendingScope,
      });
      toast.success('Âmbito de armazéns atualizado');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar âmbito');
    } finally { setSaving(false); }
  };

  const saveReferenceWarehouse = async () => {
    if (!editing || !pendingReferenceWarehouse) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${editing.id}/reference-warehouse`, {
        reference_warehouse: pendingReferenceWarehouse,
      });
      toast.success('Armazém de referência atualizado');
      fetchAll();
      setEditing((prev) => prev ? { ...prev, reference_warehouse: pendingReferenceWarehouse } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar armazém de referência');
    } finally { setSaving(false); }
  };

  const saveOverrides = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${editing.id}/permission-overrides`, {
        allow: pendingAllow,
        deny: pendingDeny,
      });
      toast.success('Overrides individuais atualizados');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar overrides');
    } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      if (pendingFullName !== editing.full_name) payload.full_name = pendingFullName;
      if (pendingPassword && pendingPassword.length >= 4) payload.password = pendingPassword;
      if (pendingActive !== (editing.is_active !== false)) payload.is_active = pendingActive;
      // Removed: default_warehouse is now managed via reference_warehouse in Armazéns tab
      if (Object.keys(payload).length === 0) {
        toast.info('Nada para guardar');
        setSaving(false);
        return;
      }
      await axios.put(`${API}/users/${editing.id}`, payload);
      toast.success('Perfil atualizado');
      setPendingPassword('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao actualizar perfil');
    } finally { setSaving(false); }
  };

  const createUser = async () => {
    if (!createForm.email || !createForm.password || !createForm.full_name) {
      toast.error('Email, password e nome são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, createForm);
      toast.success(`Utilizador ${createForm.email} criado`);
      setShowCreate(false);
      setCreateForm({ email: '', password: '', full_name: '', role: 'technician', allowed_warehouses: [] });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar utilizador');
    } finally { setSaving(false); }
  };

  const allWhCodes = warehouses.map((w) => w.code).filter(Boolean);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" /> A carregar utilizadores...
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Lock className="h-5 w-5 text-amber-600" />
            <CardTitle>Acesso restrito</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Não tens permissão <code>users.manage</code>. Pede ao admin para te dar acesso ou consulta a página em modo só-leitura.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="user-management-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Utilizadores & Permissões</h1>
                <p className="text-sm text-slate-500">
                  {users.length} utilizadores · {catalog.roles?.length || 0} perfis · {catalog.permissions?.length || 0} permissões
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchAll} disabled={refreshing} data-testid="refresh-users-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Atualizar
              </Button>
              <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#C41E3A] hover:bg-[#a01830]" data-testid="new-user-btn">
                <UserPlus className="h-4 w-4 mr-1" /> Novo utilizador
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Summary Stats — inline, sóbrio, profissional */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 border-b border-slate-200 pb-4">
          <span><span className="text-xl font-bold tabular-nums text-slate-900">{users.length}</span> Utilizadores</span>
          <span><span className="text-xl font-bold tabular-nums text-slate-900">{users.filter(u => u.role === 'admin').length}</span> Admins</span>
          <span><span className="text-xl font-bold tabular-nums text-slate-900">{users.filter(u => u.role === 'technician').length}</span> Técnicos</span>
          <span><span className="text-xl font-bold tabular-nums text-slate-900">{users.filter(u => u.role === 'store').length}</span> Lojas</span>
          {users.filter(u => !u.is_active).length > 0 && (
            <span className="text-amber-600"><span className="text-xl font-bold tabular-nums">{users.filter(u => !u.is_active).length}</span> Desactivados</span>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="users-search-input"
            />
          </div>
        </div>

        {/* Users table */}
        <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Utilizador</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Âmbito (armazéns)</th>
                  <th className="px-4 py-3">Permissões efectivas</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.filter(u => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return [u.full_name, u.name, u.email, u.role]
                    .filter(Boolean).join(' ').toLowerCase().includes(q);
                }).map((u) => {
                  const scope = u.effective_warehouses || [];
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-t hover:bg-slate-50/40" data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{u.full_name || u.email}</div>
                        <div className="text-xs text-muted-foreground">{u.email}{isMe && <span className="ml-2 text-blue-600">(tu)</span>}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'} text-xs`} data-testid={`user-role-${u.email}`}>
                          {rolesBySlug[u.role]?.label || u.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {scope.length === 0 ? (
                          <span className="text-amber-600 inline-flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> sem âmbito
                          </span>
                        ) : scope.length === allWhCodes.length ? (
                          <span className="text-emerald-700">Todos ({scope.length})</span>
                        ) : (
                          <span className="font-mono">{scope.slice(0, 4).join(', ')}{scope.length > 4 ? `… +${scope.length - 4}` : ''}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{(u.permissions || []).length} perms</td>
                      <td className="px-4 py-3">
                        {u.is_active === false ? (
                          <Badge variant="outline" className="text-xs text-slate-500">Desactivado</Badge>
                        ) : (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Activo</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                          Editar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Role catalog reference */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Catálogo de Perfis (referência)
          </CardTitle>
          <CardDescription>Permissões base associadas a cada role. Use overrides individuais para excepções pontuais.</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(catalog.roles || []).map((r) => (
            <div key={r.slug} className="border rounded-md p-3 bg-slate-50/40" data-testid={`role-card-${r.slug}`}>
              <div className="flex items-center justify-between mb-1">
                <Badge className={`${ROLE_COLORS[r.slug] || ''} text-xs`}>{r.label}</Badge>
                <span className="text-xs text-muted-foreground">{r.permissions.length} perms</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">{r.description}</p>
              <div className="text-xs text-slate-500">scope: <code>{r.warehouse_scope_mode}</code></div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar: {editing?.full_name || editing?.email}</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              {/* Tab nav */}
              <div className="flex gap-1 border-b">
                {['role', 'scope', 'overrides', 'profile'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setEditTab(tab)}
                    className={`px-3 py-2 text-sm border-b-2 ${editTab === tab ? 'border-red-600 text-red-700 font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid={`tab-${tab}`}
                  >
                    {tab === 'role' ? 'Role' : tab === 'scope' ? 'Armazéns' : tab === 'overrides' ? 'Overrides' : 'Perfil'}
                  </button>
                ))}
              </div>

              {/* ROLE TAB */}
              {editTab === 'role' && (
                <div className="space-y-3">
                  <Label>Role</Label>
                  <Select value={pendingRole} onValueChange={setPendingRole}>
                    <SelectTrigger data-testid="role-select-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(catalog.roles || []).map((r) => (
                        <SelectItem key={r.slug} value={r.slug}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {rolesBySlug[pendingRole] && (
                    <div className="text-xs text-muted-foreground bg-slate-50 border rounded p-3">
                      <p className="mb-1">{rolesBySlug[pendingRole].description}</p>
                      <p><strong>{rolesBySlug[pendingRole].permissions.length}</strong> permissões base · scope: <code>{rolesBySlug[pendingRole].warehouse_scope_mode}</code></p>
                    </div>
                  )}
                  <Button onClick={saveRole} disabled={saving || pendingRole === editing.role} data-testid="save-role-btn">
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Guardar role
                  </Button>
                </div>
              )}

              {/* SCOPE TAB */}
              {editTab === 'scope' && (
                <div className="space-y-3">
                  <Label>Armazéns permitidos (hard filter)</Label>
                  <p className="text-xs text-muted-foreground">Admin ignora esta lista — vê sempre todos. Procurement Manager pode alternar &quot;ver tudo&quot;.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto border rounded p-3 bg-slate-50/40">
                    {warehouses.map((w) => (
                      <label key={w.code} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white rounded px-2 py-1">
                        <input
                          type="checkbox"
                          checked={pendingScope.includes(w.code)}
                          onChange={(e) => {
                            if (e.target.checked) setPendingScope([...pendingScope, w.code].sort());
                            else setPendingScope(pendingScope.filter((c) => c !== w.code));
                          }}
                          data-testid={`scope-cb-${w.code}`}
                        />
                        <span className="font-mono">{w.code}</span>
                        <span className="text-muted-foreground truncate">{w.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 text-xs">
                    <Button variant="outline" size="sm" onClick={() => setPendingScope(allWhCodes)}>Todos</Button>
                    <Button variant="outline" size="sm" onClick={() => setPendingScope([])}>Nenhum</Button>
                    <span className="text-muted-foreground self-center">{pendingScope.length}/{allWhCodes.length} seleccionados</span>
                  </div>
                  <Button onClick={saveScope} disabled={saving} data-testid="save-scope-btn">
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Guardar âmbito
                  </Button>

                  {/* Armazém de Referência */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium">Armazém de Referência</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      O armazém que define o contexto operacional do utilizador (inventário, movimentos, filtros).
                      <br/>
                      <span className="text-amber-600">Nota: Não afeta o Procurement Intelligence que usa sempre o armazém central (001).</span>
                    </p>
                    <div className="flex gap-2 items-center">
                      <Select
                        value={pendingReferenceWarehouse}
                        onValueChange={setPendingReferenceWarehouse}
                      >
                        <SelectTrigger className="w-64" data-testid="reference-warehouse-select">
                          <SelectValue placeholder="Selecionar armazém..." />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Show all warehouses - reference warehouse is just operational context, not access control */}
                          {warehouses.map((w) => (
                            <SelectItem key={w.code} value={w.code}>
                              <span className="font-mono">{w.code}</span> — {w.name || w.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        onClick={saveReferenceWarehouse} 
                        disabled={saving || !pendingReferenceWarehouse} 
                        size="sm"
                        data-testid="save-reference-warehouse-btn"
                      >
                        {saving ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : null}
                        Guardar
                      </Button>
                    </div>
                    {editing?.reference_warehouse && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Atual: <span className="font-mono font-medium">{editing.reference_warehouse}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* OVERRIDES TAB */}
              {editTab === 'overrides' && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Excepções pontuais à matriz de role. <strong className="text-emerald-700">Allow</strong>: adiciona permissão extra. <strong className="text-red-700">Deny</strong>: remove uma permissão herdada do role.
                  </p>
                  <div className="border rounded max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr><th className="px-2 py-1 text-left">Permissão</th><th className="px-2 py-1">Allow</th><th className="px-2 py-1">Deny</th></tr>
                      </thead>
                      <tbody>
                        {(catalog.permissions || []).map((p) => {
                          const inRole = rolesBySlug[editing.role]?.permissions.includes(p.slug);
                          return (
                            <tr key={p.slug} className="border-t">
                              <td className="px-2 py-1">
                                <div className="font-mono text-[10px] text-slate-500">{p.slug}</div>
                                <div>{p.label}{inRole && <span className="ml-2 text-emerald-600 text-[10px]">(role)</span>}</div>
                              </td>
                              <td className="px-2 py-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={pendingAllow.includes(p.slug)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setPendingAllow([...pendingAllow, p.slug]);
                                      setPendingDeny(pendingDeny.filter((x) => x !== p.slug));
                                    } else setPendingAllow(pendingAllow.filter((x) => x !== p.slug));
                                  }}
                                />
                              </td>
                              <td className="px-2 py-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={pendingDeny.includes(p.slug)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setPendingDeny([...pendingDeny, p.slug]);
                                      setPendingAllow(pendingAllow.filter((x) => x !== p.slug));
                                    } else setPendingDeny(pendingDeny.filter((x) => x !== p.slug));
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button onClick={saveOverrides} disabled={saving} data-testid="save-overrides-btn">
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Guardar overrides ({pendingAllow.length} allow · {pendingDeny.length} deny)
                  </Button>
                </div>
              )}

              {/* PROFILE TAB */}
              {editTab === 'profile' && (
                <div className="space-y-3">
                  <div>
                    <Label>Nome completo</Label>
                    <Input value={pendingFullName} onChange={(e) => setPendingFullName(e.target.value)} data-testid="edit-fullname-input" />
                  </div>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    O Armazém de Referência é agora configurado no separador "Armazéns".
                  </p>
                  <div>
                    <Label className="flex items-center gap-2"><KeyRound className="h-3 w-3" /> Nova password (opcional)</Label>
                    <Input type="password" value={pendingPassword} onChange={(e) => setPendingPassword(e.target.value)} placeholder="deixar vazio para manter" data-testid="edit-password-input" />
                  </div>
                  <div className="flex items-center justify-between border rounded p-3 bg-slate-50/40">
                    <div>
                      <div className="font-medium text-sm">Conta activa</div>
                      <div className="text-xs text-muted-foreground">Quando desactivada, login é rejeitado.</div>
                    </div>
                    <Switch checked={pendingActive} onCheckedChange={setPendingActive} data-testid="edit-active-switch" />
                  </div>
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Guardar perfil
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Novo utilizador</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Email</Label><Input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="create-email-input" /></div>
            <div><Label>Nome completo</Label><Input value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} data-testid="create-fullname-input" /></div>
            <div><Label>Password</Label><Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="create-password-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="create-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog.roles || []).map((r) => (<SelectItem key={r.slug} value={r.slug}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
              {rolesBySlug[createForm.role] && (
                <p className="text-xs text-muted-foreground mt-1">{rolesBySlug[createForm.role].description}</p>
              )}
            </div>
            <div>
              <Label>Armazéns permitidos (opcional)</Label>
              <div className="grid grid-cols-3 gap-1 max-h-40 overflow-y-auto border rounded p-2">
                {warehouses.map((w) => (
                  <label key={w.code} className="text-xs flex items-center gap-1">
                    <input type="checkbox"
                      checked={createForm.allowed_warehouses.includes(w.code)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...createForm.allowed_warehouses, w.code]
                          : createForm.allowed_warehouses.filter((c) => c !== w.code);
                        setCreateForm({ ...createForm, allowed_warehouses: next });
                      }}
                    />
                    <span className="font-mono">{w.code}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={createUser} disabled={saving} className="bg-[#C41E3A] hover:bg-[#a01830]" data-testid="create-user-confirm-btn">
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Criar utilizador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
